<template>
  <div class="flex flex-col p-6 gap-6">
    <h2 data-cy="title-trade-ideas" class="text-2xl font-bold">Trade ideas</h2>

    <div class="flex items-end justify-between">
      <!-- TAB (Em andamento / Histórico) -->
      <div class="flex items-start">
        <button
          v-for="nav in navigation"
          :key="nav"
          class="px-4 py-2 border-b-2 transition-all"
          :class="[activeTab === nav ? 'border-ontick-blue-600 font-bold' : 'border-ontick-steel-500 font-normal']"
          @click="activeTab = nav"
        >
          {{ nav }}
        </button>
      </div>
      <div class="flex items-center justify-end gap-2">
        <!-- REFRESH -->
        <refresh-button-component class="btn" @click="refreshTradeIdea" />

        <!-- Página de pré-autorização -->
        <router-link :to="{ name: 'Pré-autorização Trade Ideas v2' }" class="h-full">
          <button
            content="Pré-autorização"
            v-tippy="{ arrow: true, theme: 'green' }"
            class="btn text-ontick-blue-600 h-full bg-ontick-steel-500 w-[40px]"
          >
            <i v-html="$icons.clipboard"></i>
          </button>
        </router-link>
      </div>
    </div>

    <KeepAlive>
      <transition name="fade" mode="out-in">
        <TableOngoing
          v-if="activeTab === 'Em andamento'"
          @details="detailsOngoing"
          @stop-strategy="openModalStopStrategy"
          @stop-trade-idea="openModalStopTradeIdea"
        />
        <TableHistory v-else @details="detailsHistory" />
      </transition>
    </KeepAlive>

    <portal to="modal">
      <transition name="fade">
        <template v-if="modalStop.isOpen">
          <!-- Modal de STOP -->
          <ModalStopStrategy
            v-if="modalStop.type === 'strategy'"
            @close="closeModalStop"
            @confirm="confirmStopStrategy"
          />
          <ModalStopTradeIdea
            v-if="modalStop.type === 'trade-idea'"
            @close="closeModalStop"
            @confirm="confirmStopTradeIdea"
          />
        </template>
      </transition>
      <transition name="fade">
        <!-- Modal de detalhes -->
        <modal-details-component v-if="modalDetails.isOpen" :tradeIdea="modalDetails.data" @close="closeModalDetails" />
      </transition>
    </portal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: "TradeIdeas",

  components: {
    RefreshButtonComponent: () => import("../../../shared/components/refresh-button/refresh-button.component.vue"),
    ModalDetailsComponent: () => import("../components/modal-details.component.vue"),
    TableOngoing: () => import("../components/table-ongoing.component.vue"),
    TableHistory: () => import("../components/table-history.component.vue"),
    ModalStopStrategy: () => import("../components/modal-stop-strategy.component.vue"),
    ModalStopTradeIdea: () => import("../components/modal-stop-trade-idea.component.vue"),
  },

  data() {
    return {
      navigation: ["Em andamento", "Histórico"],
      activeTab: "Em andamento",

      // Modal de stop
      modalStop: {
        isOpen: false,
        data: null,
      },

      // Modal de detalhes
      modalDetails: {
        isOpen: false,
        type: null, // 'strategy' ou 'trade-idea'
        data: null,
      },
    };
  },

  computed: {
    ...mapGetters({
      isLoading: "tradeIdeas/isLoading",
      getSelectedAccount: "accountTypes/getSelectedAccount",
    }),
  },

  watch: {
    getSelectedAccount: {
      deep: true,
      handler() {
        this.refreshTradeIdea();
      },
    },
  },

  methods: {
    ...mapState({
      selectedAccount: (state) => state.accountTypes.selectedAccount,
    }),
    ...mapActions({
      listTradeIdeas: "tradeIdeas/listTradeIdeas",
      listHistoryTradeIdeas: "tradeIdeas/listHistoryTradeIdeas",
      stopStrategy: "tradeIdeas/stopStrategy",
      stopTradeIdea: "tradeIdeas/stopTradeIdea",
    }),

    refreshTradeIdea() {
      if (this.activeTab === "Em andamento") {
        this.listTradeIdeas();
      } else {
        this.listHistoryTradeIdeas();
      }
    },

    detailsOngoing(ti) {
      /*
      Comentário
      ti_comment: ti_comment
      -------------
      Operação
      ti_symbol: ti_symbol
      ti_direction: ti_direction
      -------------
      Status
      ti_status: ti_status
      -------------
      Tipo
      ti_style: ti_style
      -------------
      Início
      ti_start_datetime: ti_start_datetime
      -------------
      Analista técnico
      strategy_analyst: strategy_analyst
      -------------
      Entrada
      to_entry_price: to_info.to_entry_price
      -------------
      TP
      ti_take_profit_price: ti_take_profit_price
      -------------
      SL
      ti_stop_loss_price: ti_stop_loss_price
      -------------
      Financeiro alocado
      to_allocated_value: to_info.to_allocated_value
      to_allocated_percent: to_info.to_allocated_percent
      -------------
      Resultado
      to_pnl: to_info.to_pnl
       */

      const modelData = {
        // Comentário
        ti_comment: ti.ti_comment,
        // Operação
        ti_symbol: ti.ti_symbol,
        ti_direction: ti.ti_direction,
        // Status
        ti_status: ti.ti_status,
        // Tipo
        ti_style: ti.ti_style,
        // Início
        ti_start_datetime: ti.ti_start_datetime,
        // Analista técnico
        strategy_analyst: ti.strategy_analyst,
        // TP
        ti_take_profit_price: ti.ti_take_profit_price,
        // SL
        ti_stop_loss_price: ti.ti_stop_loss_price,
        // Imagens
        ti_images: ti.ti_images,
        // --------------------------
        to_info: {
          // Entrada
          to_entry_price: ti.to_entry_price || 0,

          // Financeiro alocado
          to_allocated_value: ti.to_allocated_value || 0,
          to_allocated_percent: ti.to_allocated_percent || 0,

          // Resultado
          to_pnl: ti.to_pnl || 0,
        },
      };

      this.modalDetails.data = modelData;
      this.modalDetails.isOpen = true;
    },

    detailsHistory(strat) {
      this.modalDetails.data = strat;
      this.modalDetails.isOpen = true;
    },

    closeModalDetails() {
      this.modalDetails.isOpen = false;
      this.modalDetails.data = null;
    },

    openModalStopStrategy({ strat }) {
      this.modalStop.type = "strategy";
      this.modalStop.isOpen = true;
      this.modalStop.data = { strat };
    },

    openModalStopTradeIdea({ ti }) {
      this.modalStop.type = "trade-idea";
      this.modalStop.isOpen = true;
      this.modalStop.data = ti;
    },

    closeModalStop() {
      this.modalStop.isOpen = false;
      this.modalStop.type = null;
      this.modalStop.data = null;
    },

    async confirmStopTradeIdea() {
      const body = {
        auth_strat_id: this.modalStop.data.auth_strat_id,
        ti_id: this.modalStop.data.ti_id,
      };

      try {
        const response = await this.stopTradeIdea(body);

        if (response.status === "success") {
          this.closeModalStop();
          this.refreshTradeIdea();
          setTimeout(() => {
            this.$swal({
              title: response.msg,
              icon: "success",
              iconHtml: `
                <svg xmlns="http://www.w3.org/2000/svg" width="104" height="104" viewBox="0 0 104 104" fill="none">
                  <g clip-path="url(#clip0_14421_20487)">
                    <path d="M9 52.3333C9 28.4133 28.4133 9 52.3333 9C76.2533 9 95.6667 28.4133 95.6667 52.3333C95.6667 76.2533 76.2533 95.6667 52.3333 95.6667C28.4133 95.6667 9 76.2533 9 52.3333Z" fill="white"/>
                    <path d="M52.3333 9C28.4133 9 9 28.4133 9 52.3333C9 76.2533 28.4133 95.6667 52.3333 95.6667C76.2533 95.6667 95.6667 76.2533 95.6667 52.3333C95.6667 28.4133 76.2533 9 52.3333 9ZM40.59 70.9233L25.0333 55.3667C23.3433 53.6767 23.3433 50.9467 25.0333 49.2567C26.7233 47.5667 29.4533 47.5667 31.1433 49.2567L43.6667 61.7367L73.48 31.9233C75.17 30.2333 77.9 30.2333 79.59 31.9233C81.28 33.6133 81.28 36.3433 79.59 38.0333L46.7 70.9233C45.0533 72.6133 42.28 72.6133 40.59 70.9233Z" fill="#1EB866"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_14421_20487">
                      <rect width="104" height="104" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
              `,
              showConfirmButton: "OK",
              showDenyButton: false,
              showCancelButton: false,
            });
          }, 500);
        }
      } catch (error) {
        if (error.status === "error") {
          this.closeModalStop();
          setTimeout(() => {
            this.$swal({
              title: "Erro",
              text: error.msg,
              icon: "error",
              iconHtml: `
                <svg xmlns="http://www.w3.org/2000/svg" width="87" height="88" viewBox="0 0 87 88" fill="none">
                  <path d="M43.5 65.6667C44.7277 65.6667 45.7569 65.2515 46.5875 64.4209C47.418 63.5904 47.8333 62.5612 47.8333 61.3334C47.8333 60.1056 47.418 59.0765 46.5875 58.2459C45.7569 57.4154 44.7277 57.0001 43.5 57.0001C42.2722 57.0001 41.243 57.4154 40.4125 58.2459C39.5819 59.0765 39.1666 60.1056 39.1666 61.3334C39.1666 62.5612 39.5819 63.5904 40.4125 64.4209C41.243 65.2515 42.2722 65.6667 43.5 65.6667ZM43.5 48.3334C44.7277 48.3334 45.7569 47.9181 46.5875 47.0876C47.418 46.257 47.8333 45.2279 47.8333 44.0001V26.6667C47.8333 25.439 47.418 24.4098 46.5875 23.5792C45.7569 22.7487 44.7277 22.3334 43.5 22.3334C42.2722 22.3334 41.243 22.7487 40.4125 23.5792C39.5819 24.4098 39.1666 25.439 39.1666 26.6667V44.0001C39.1666 45.2279 39.5819 46.257 40.4125 47.0876C41.243 47.9181 42.2722 48.3334 43.5 48.3334ZM43.5 87.3334C37.5055 87.3334 31.8722 86.1959 26.6 83.9209C21.3277 81.6459 16.7416 78.5584 12.8416 74.6584C8.94163 70.7584 5.85413 66.1723 3.57913 60.9001C1.30413 55.6279 0.166626 49.9945 0.166626 44.0001C0.166626 38.0056 1.30413 32.3723 3.57913 27.1001C5.85413 21.8279 8.94163 17.2417 12.8416 13.3417C16.7416 9.44175 21.3277 6.35425 26.6 4.07925C31.8722 1.80425 37.5055 0.666748 43.5 0.666748C49.4944 0.666748 55.1277 1.80425 60.4 4.07925C65.6722 6.35425 70.2583 9.44175 74.1583 13.3417C78.0583 17.2417 81.1458 21.8279 83.4208 27.1001C85.6958 32.3723 86.8333 38.0056 86.8333 44.0001C86.8333 49.9945 85.6958 55.6279 83.4208 60.9001C81.1458 66.1723 78.0583 70.7584 74.1583 74.6584C70.2583 78.5584 65.6722 81.6459 60.4 83.9209C55.1277 86.1959 49.4944 87.3334 43.5 87.3334Z" fill="#DD5B5B"/>
                </svg>
              `,
              showConfirmButton: "OK",
              showDenyButton: false,
              showCancelButton: false,
            });
          }, 500);
        }
      }
    },

    async confirmStopStrategy() {
      try {
        const response = await this.stopStrategy({
          auth_strat_id: this.modalStop.data.strat.auth_strat_id,
        });

        if (response.status === "success") {
          this.closeModalStop();
          this.refreshTradeIdea();
          setTimeout(() => {
            this.$swal({
              title: response.msg,
              icon: "success",
              iconHtml: `
                <svg xmlns="http://www.w3.org/2000/svg" width="104" height="104" viewBox="0 0 104 104" fill="none">
                  <g clip-path="url(#clip0_14421_20487)">
                    <path d="M9 52.3333C9 28.4133 28.4133 9 52.3333 9C76.2533 9 95.6667 28.4133 95.6667 52.3333C95.6667 76.2533 76.2533 95.6667 52.3333 95.6667C28.4133 95.6667 9 76.2533 9 52.3333Z" fill="white"/>
                    <path d="M52.3333 9C28.4133 9 9 28.4133 9 52.3333C9 76.2533 28.4133 95.6667 52.3333 95.6667C76.2533 95.6667 95.6667 76.2533 95.6667 52.3333C95.6667 28.4133 76.2533 9 52.3333 9ZM40.59 70.9233L25.0333 55.3667C23.3433 53.6767 23.3433 50.9467 25.0333 49.2567C26.7233 47.5667 29.4533 47.5667 31.1433 49.2567L43.6667 61.7367L73.48 31.9233C75.17 30.2333 77.9 30.2333 79.59 31.9233C81.28 33.6133 81.28 36.3433 79.59 38.0333L46.7 70.9233C45.0533 72.6133 42.28 72.6133 40.59 70.9233Z" fill="#1EB866"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_14421_20487">
                      <rect width="104" height="104" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
              `,
              showConfirmButton: "OK",
              showDenyButton: false,
              showCancelButton: false,
            });
          }, 500);
        }
      } catch (error) {
        if (error.status === "error") {
          this.closeModalStop();
          setTimeout(() => {
            this.$swal({
              title: "Erro",
              text: error.msg,
              icon: "error",
              iconHtml: `
                <svg xmlns="http://www.w3.org/2000/svg" width="87" height="88" viewBox="0 0 87 88" fill="none">
                  <path d="M43.5 65.6667C44.7277 65.6667 45.7569 65.2515 46.5875 64.4209C47.418 63.5904 47.8333 62.5612 47.8333 61.3334C47.8333 60.1056 47.418 59.0765 46.5875 58.2459C45.7569 57.4154 44.7277 57.0001 43.5 57.0001C42.2722 57.0001 41.243 57.4154 40.4125 58.2459C39.5819 59.0765 39.1666 60.1056 39.1666 61.3334C39.1666 62.5612 39.5819 63.5904 40.4125 64.4209C41.243 65.2515 42.2722 65.6667 43.5 65.6667ZM43.5 48.3334C44.7277 48.3334 45.7569 47.9181 46.5875 47.0876C47.418 46.257 47.8333 45.2279 47.8333 44.0001V26.6667C47.8333 25.439 47.418 24.4098 46.5875 23.5792C45.7569 22.7487 44.7277 22.3334 43.5 22.3334C42.2722 22.3334 41.243 22.7487 40.4125 23.5792C39.5819 24.4098 39.1666 25.439 39.1666 26.6667V44.0001C39.1666 45.2279 39.5819 46.257 40.4125 47.0876C41.243 47.9181 42.2722 48.3334 43.5 48.3334ZM43.5 87.3334C37.5055 87.3334 31.8722 86.1959 26.6 83.9209C21.3277 81.6459 16.7416 78.5584 12.8416 74.6584C8.94163 70.7584 5.85413 66.1723 3.57913 60.9001C1.30413 55.6279 0.166626 49.9945 0.166626 44.0001C0.166626 38.0056 1.30413 32.3723 3.57913 27.1001C5.85413 21.8279 8.94163 17.2417 12.8416 13.3417C16.7416 9.44175 21.3277 6.35425 26.6 4.07925C31.8722 1.80425 37.5055 0.666748 43.5 0.666748C49.4944 0.666748 55.1277 1.80425 60.4 4.07925C65.6722 6.35425 70.2583 9.44175 74.1583 13.3417C78.0583 17.2417 81.1458 21.8279 83.4208 27.1001C85.6958 32.3723 86.8333 38.0056 86.8333 44.0001C86.8333 49.9945 85.6958 55.6279 83.4208 60.9001C81.1458 66.1723 78.0583 70.7584 74.1583 74.6584C70.2583 78.5584 65.6722 81.6459 60.4 83.9209C55.1277 86.1959 49.4944 87.3334 43.5 87.3334Z" fill="#DD5B5B"/>
                </svg>
              `,
              showConfirmButton: "OK",
              showDenyButton: false,
              showCancelButton: false,
            });
          }, 500);
        }
      }
    },
  },
};

// SUCCESS ALERT
// this.$swal({
//   title: "Investimento parado<br />com sucesso",
//   icon: "success",
//   iconHtml: `
//     <svg xmlns="http://www.w3.org/2000/svg" width="104" height="104" viewBox="0 0 104 104" fill="none">
//       <g clip-path="url(#clip0_14421_20487)">
//         <path d="M9 52.3333C9 28.4133 28.4133 9 52.3333 9C76.2533 9 95.6667 28.4133 95.6667 52.3333C95.6667 76.2533 76.2533 95.6667 52.3333 95.6667C28.4133 95.6667 9 76.2533 9 52.3333Z" fill="white"/>
//         <path d="M52.3333 9C28.4133 9 9 28.4133 9 52.3333C9 76.2533 28.4133 95.6667 52.3333 95.6667C76.2533 95.6667 95.6667 76.2533 95.6667 52.3333C95.6667 28.4133 76.2533 9 52.3333 9ZM40.59 70.9233L25.0333 55.3667C23.3433 53.6767 23.3433 50.9467 25.0333 49.2567C26.7233 47.5667 29.4533 47.5667 31.1433 49.2567L43.6667 61.7367L73.48 31.9233C75.17 30.2333 77.9 30.2333 79.59 31.9233C81.28 33.6133 81.28 36.3433 79.59 38.0333L46.7 70.9233C45.0533 72.6133 42.28 72.6133 40.59 70.9233Z" fill="#1EB866"/>
//       </g>
//       <defs>
//         <clipPath id="clip0_14421_20487">
//           <rect width="104" height="104" fill="white"/>
//         </clipPath>
//       </defs>
//     </svg>
//   `,
//   showConfirmButton: "OK",
//   showDenyButton: false,
//   showCancelButton: false,
// });

// ERROR ALERT
// this.$swal({
//   title: "Erro",
//   text: "Assinatura eletrônica incorreta",
//   icon: "error",
//   iconHtml: `
//     <svg xmlns="http://www.w3.org/2000/svg" width="87" height="88" viewBox="0 0 87 88" fill="none">
//       <path d="M43.5 65.6667C44.7277 65.6667 45.7569 65.2515 46.5875 64.4209C47.418 63.5904 47.8333 62.5612 47.8333 61.3334C47.8333 60.1056 47.418 59.0765 46.5875 58.2459C45.7569 57.4154 44.7277 57.0001 43.5 57.0001C42.2722 57.0001 41.243 57.4154 40.4125 58.2459C39.5819 59.0765 39.1666 60.1056 39.1666 61.3334C39.1666 62.5612 39.5819 63.5904 40.4125 64.4209C41.243 65.2515 42.2722 65.6667 43.5 65.6667ZM43.5 48.3334C44.7277 48.3334 45.7569 47.9181 46.5875 47.0876C47.418 46.257 47.8333 45.2279 47.8333 44.0001V26.6667C47.8333 25.439 47.418 24.4098 46.5875 23.5792C45.7569 22.7487 44.7277 22.3334 43.5 22.3334C42.2722 22.3334 41.243 22.7487 40.4125 23.5792C39.5819 24.4098 39.1666 25.439 39.1666 26.6667V44.0001C39.1666 45.2279 39.5819 46.257 40.4125 47.0876C41.243 47.9181 42.2722 48.3334 43.5 48.3334ZM43.5 87.3334C37.5055 87.3334 31.8722 86.1959 26.6 83.9209C21.3277 81.6459 16.7416 78.5584 12.8416 74.6584C8.94163 70.7584 5.85413 66.1723 3.57913 60.9001C1.30413 55.6279 0.166626 49.9945 0.166626 44.0001C0.166626 38.0056 1.30413 32.3723 3.57913 27.1001C5.85413 21.8279 8.94163 17.2417 12.8416 13.3417C16.7416 9.44175 21.3277 6.35425 26.6 4.07925C31.8722 1.80425 37.5055 0.666748 43.5 0.666748C49.4944 0.666748 55.1277 1.80425 60.4 4.07925C65.6722 6.35425 70.2583 9.44175 74.1583 13.3417C78.0583 17.2417 81.1458 21.8279 83.4208 27.1001C85.6958 32.3723 86.8333 38.0056 86.8333 44.0001C86.8333 49.9945 85.6958 55.6279 83.4208 60.9001C81.1458 66.1723 78.0583 70.7584 74.1583 74.6584C70.2583 78.5584 65.6722 81.6459 60.4 83.9209C55.1277 86.1959 49.4944 87.3334 43.5 87.3334Z" fill="#DD5B5B"/>
//     </svg>
//   `,
//   showConfirmButton: "OK",
//   showDenyButton: false,
//   showCancelButton: false,
// });
</script>

<style lang="scss" scoped>
.btn {
  @apply font-medium flex items-center justify-center h-10 rounded-lg px-4 gap-2 transition-all hover:brightness-125;
}
.fade {
  &-enter,
  &-leave-to {
    @apply scale-125 opacity-0;
  }
  &-enter-active,
  &-leave-active {
    @apply transition-all duration-300 ease-in-out;
  }
}
</style>
